import { useState, useCallback, useMemo } from 'react'
import type { ReviewFilters, ReviewSort } from '@/types/reviews'

interface UseReviewsFiltersResult {
  filters: ReviewFilters
  sort: ReviewSort
  page: number
  pageSize: number
  
  // Filter setters
  setSearch: (search: string) => void
  setLocationId: (locationId: string | undefined) => void
  setServiceId: (serviceId: string | undefined) => void
  setRatingRange: (min?: number, max?: number) => void
  setDateRange: (from?: string, to?: string) => void
  setResponseFilter: (hasResponse?: boolean, responseNeeded?: boolean) => void
  
  // Sort setters
  setSort: (sort: ReviewSort) => void
  
  // Pagination
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
  
  // Reset functions
  clearFilters: () => void
  clearAllFilters: () => void
  
  // Computed values
  hasActiveFilters: boolean
  filterCount: number
}

const defaultFilters: ReviewFilters = {}
const defaultSort: ReviewSort = { field: 'reviewer_create_time', direction: 'desc' }

export function useReviewsFilters(): UseReviewsFiltersResult {
  const [filters, setFilters] = useState<ReviewFilters>(defaultFilters)
  const [sort, setSort] = useState<ReviewSort>(defaultSort)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Filter setters
  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search: search || undefined }))
    setPage(1) // Reset to first page when filtering
  }, [])

  const setLocationId = useCallback((locationId: string | undefined) => {
    setFilters(prev => ({ ...prev, location_id: locationId }))
    setPage(1)
  }, [])

  const setServiceId = useCallback((serviceId: string | undefined) => {
    setFilters(prev => ({ ...prev, service_id: serviceId }))
    setPage(1)
  }, [])

  const setRatingRange = useCallback((min?: number, max?: number) => {
    setFilters(prev => ({ 
      ...prev, 
      rating_min: min, 
      rating_max: max 
    }))
    setPage(1)
  }, [])

  const setDateRange = useCallback((from?: string, to?: string) => {
    setFilters(prev => ({ 
      ...prev, 
      date_from: from, 
      date_to: to 
    }))
    setPage(1)
  }, [])

  const setResponseFilter = useCallback((hasResponse?: boolean, responseNeeded?: boolean) => {
    setFilters(prev => ({ 
      ...prev, 
      has_response: hasResponse,
      response_needed: responseNeeded
    }))
    setPage(1)
  }, [])

  // Clear individual filter categories
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters)
    setPage(1)
  }, [])

  // Clear everything including sort and pagination
  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters)
    setSort(defaultSort)
    setPage(1)
    setPageSize(20)
  }, [])

  // Sort setter
  const setSortHandler = useCallback((newSort: ReviewSort) => {
    setSort(newSort)
    setPage(1) // Reset to first page when sorting
  }, [])

  // Pagination setters
  const setPageHandler = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const setPageSizeHandler = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page when changing page size
  }, [])

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => 
      value !== undefined && value !== null && value !== ''
    )
  }, [filters])

  const filterCount = useMemo(() => {
    return Object.values(filters).filter(value => 
      value !== undefined && value !== null && value !== ''
    ).length
  }, [filters])

  return {
    filters,
    sort,
    page,
    pageSize,
    
    // Filter setters
    setSearch,
    setLocationId,
    setServiceId,
    setRatingRange,
    setDateRange,
    setResponseFilter,
    
    // Sort setters
    setSort: setSortHandler,
    
    // Pagination
    setPage: setPageHandler,
    setPageSize: setPageSizeHandler,
    
    // Reset functions
    clearFilters,
    clearAllFilters,
    
    // Computed values
    hasActiveFilters,
    filterCount,
  }
}